/**
 * Navbar user menu: profile, logout, and profile edit modal.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useAuth } from '@/hooks/useAuth';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EditProfileModal } from '@/components/profile/EditProfileModal';
import { getPrimaryAuthRole } from '@/utils/primary-role-utils';

/** Avatar dropdown: profile (modal for staff, route otherwise) and logout. */
export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, roles } = useAuthStore();
  const { logout } = useAuth();
  const [isProfileModalOpen, setIsProfileModalOpen] = React.useState(false);
  const [cachedUserName, setCachedUserName] = React.useState<string>('');
  const [cachedRole, setCachedRole] = React.useState<string>('');

  /** Two-letter initials from first/last name, else the display-name prefix. */
  const initials = React.useMemo(() => {
    if (!user?.firstName || !user?.lastName) {
      return user?.displayName?.substring(0, 2).toUpperCase() || '??';
    }
    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
  }, [user]);

  /** Display role label for the dropdown subtitle (highest roleGroup wins). */
  const primaryRole = React.useMemo(() => {
    return getPrimaryAuthRole(roles)?.label || 'Utilisateur';
  }, [roles]);

  /** Keep a last-known name so the menu header does not flash empty during refetch. */
  React.useEffect(() => {
    if (user) {
      const displayName =
        user.displayName ||
        (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : 'Utilisateur');
      setCachedUserName(displayName);
    }
  }, [user]);

  /** Keep a last-known role label so the subtitle stays stable during refetch. */
  React.useEffect(() => {
    if (primaryRole) {
      setCachedRole(primaryRole);
    }
  }, [primaryRole]);

  /** Admins and volunteers edit via modal; other roles go to `/profile`. */
  const shouldShowProfileModal = React.useMemo(() => {
    if (!roles || roles.length === 0) return false;
    return roles.some((r) => r.roleGroup === 'admins' || r.roleGroup === 'volunteers');
  }, [roles]);

  /** Sign out then always land on `/login`, even if logout throws. */
  const handleLogout = async () => {
    try {
      await logout();
      void navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      void navigate('/login');
    }
  };

  /** Open the staff profile modal or navigate to the full profile page. */
  const handleProfileClick = () => {
    if (shouldShowProfileModal) {
      setIsProfileModalOpen(true);
    } else {
      void navigate('/profile');
    }
  };

  /** Close the staff profile modal. */
  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="user-menu-button" aria-label="Menu utilisateur">
            {initials}
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56 user-menu-content !origin-top-right">
          <div className="px-3 py-2 border-b">
            <p className="font-medium text-sm">{cachedUserName || 'Utilisateur'}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{cachedRole || 'Utilisateur'}</p>
          </div>

          <DropdownMenuItem onClick={handleProfileClick} className="cursor-pointer">
            <User className="mr-2 h-4 w-4" />
            Mon Profil
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem
            onClick={handleLogout}
            className="cursor-pointer text-destructive focus:text-destructive user-menu-item-logout"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Déconnexion
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {shouldShowProfileModal && (
        <EditProfileModal open={isProfileModalOpen} onClose={handleCloseProfileModal} />
      )}
    </>
  );
};
